import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Grid3X3, 
  Save, 
  Trash2, 
  ArrowLeft,
  Package,
  Route,
  DoorOpen,
  Settings
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TireMapEditor = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [gridWidth, setGridWidth] = useState(10);
  const [gridHeight, setGridHeight] = useState(10);
  const [tiles, setTiles] = useState({});
  const [selectedTile, setSelectedTile] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Drag & Drop state
  const [draggedTile, setDraggedTile] = useState(null);
  const [dragOverTile, setDragOverTile] = useState(null);

  // Edit form state
  const [editForm, setEditForm] = useState({
    tile_type: "storage",
    name: "",
    capacity: 4
  });

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Load settings and tiles
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load grid settings
        const settingsRes = await axios.get(`${API}/tiremap/settings`);
        setGridWidth(settingsRes.data.grid_width || 10);
        setGridHeight(settingsRes.data.grid_height || 10);

        // Load tiles
        const tilesRes = await axios.get(`${API}/tiremap/tiles`);
        const tilesMap = {};
        tilesRes.data.forEach(tile => {
          tilesMap[`${tile.x}-${tile.y}`] = tile;
        });
        setTiles(tilesMap);
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Fehler beim Laden der Daten");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Save grid settings
  const saveSettings = async () => {
    try {
      await axios.post(`${API}/tiremap/settings`, {
        grid_width: gridWidth,
        grid_height: gridHeight
      }, authHeaders);
      toast.success("Einstellungen gespeichert");
      setSettingsDialog(false);
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  // Handle tile click
  const handleTileClick = (x, y) => {
    const key = `${x}-${y}`;
    const existingTile = tiles[key];
    
    setSelectedTile({ x, y, existing: existingTile });
    
    if (existingTile) {
      setEditForm({
        tile_type: existingTile.tile_type,
        name: existingTile.name || "",
        capacity: existingTile.capacity || 4
      });
    } else {
      setEditForm({
        tile_type: "storage",
        name: "",
        capacity: 4
      });
    }
    
    setEditDialog(true);
  };

  // Save tile
  const saveTile = async () => {
    if (!selectedTile) return;

    try {
      const tileData = {
        x: selectedTile.x,
        y: selectedTile.y,
        tile_type: editForm.tile_type,
        name: editForm.tile_type === "storage" ? editForm.name : null,
        capacity: editForm.tile_type === "storage" ? editForm.capacity : 0
      };

      await axios.post(`${API}/tiremap/tiles`, tileData, authHeaders);
      
      // Update local state
      const key = `${selectedTile.x}-${selectedTile.y}`;
      setTiles(prev => ({
        ...prev,
        [key]: { ...tileData, id: prev[key]?.id || Date.now().toString() }
      }));

      toast.success("Kachel gespeichert");
      setEditDialog(false);
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  // Delete tile
  const deleteTile = async () => {
    if (!selectedTile) return;

    try {
      await axios.delete(`${API}/tiremap/tiles/${selectedTile.x}/${selectedTile.y}`, authHeaders);
      
      const key = `${selectedTile.x}-${selectedTile.y}`;
      setTiles(prev => {
        const newTiles = { ...prev };
        delete newTiles[key];
        return newTiles;
      });

      toast.success("Kachel gelöscht");
      setEditDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e, x, y) => {
    const key = `${x}-${y}`;
    const tile = tiles[key];
    if (!tile) return;
    
    setDraggedTile({ x, y, tile });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  };

  const handleDragOver = (e, x, y) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTile({ x, y });
  };

  const handleDragLeave = () => {
    setDragOverTile(null);
  };

  const handleDrop = async (e, targetX, targetY) => {
    e.preventDefault();
    setDragOverTile(null);
    
    if (!draggedTile) return;
    
    const { x: sourceX, y: sourceY, tile } = draggedTile;
    const targetKey = `${targetX}-${targetY}`;
    
    // Don't drop on same position
    if (sourceX === targetX && sourceY === targetY) {
      setDraggedTile(null);
      return;
    }
    
    // Check if target is occupied
    if (tiles[targetKey]) {
      toast.error("Zielposition ist bereits belegt");
      setDraggedTile(null);
      return;
    }
    
    try {
      // Delete old tile
      await axios.delete(`${API}/tiremap/tiles/${sourceX}/${sourceY}`, authHeaders);
      
      // Create new tile at target position
      const newTileData = {
        x: targetX,
        y: targetY,
        tile_type: tile.tile_type,
        name: tile.name,
        capacity: tile.capacity
      };
      
      await axios.post(`${API}/tiremap/tiles`, newTileData, authHeaders);
      
      // Update local state
      const sourceKey = `${sourceX}-${sourceY}`;
      setTiles(prev => {
        const newTiles = { ...prev };
        delete newTiles[sourceKey];
        newTiles[targetKey] = { ...tile, x: targetX, y: targetY };
        return newTiles;
      });
      
      toast.success("Kachel verschoben");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Verschieben");
    }
    
    setDraggedTile(null);
  };

  const handleDragEnd = () => {
    setDraggedTile(null);
    setDragOverTile(null);
  };

  // Get tile color based on type
  const getTileColor = (tile) => {
    if (!tile) return "bg-white hover:bg-gray-100";
    switch (tile.tile_type) {
      case "storage":
        return "bg-green-100 hover:bg-green-200 border-green-400";
      case "path":
        return "bg-gray-200 hover:bg-gray-300 border-gray-400";
      case "entrance":
        return "bg-blue-100 hover:bg-blue-200 border-blue-400";
      default:
        return "bg-white hover:bg-gray-100";
    }
  };

  // Get tile icon
  const getTileIcon = (tile) => {
    if (!tile) return null;
    switch (tile.tile_type) {
      case "storage":
        return <Package className="w-4 h-4 text-green-600" />;
      case "path":
        return <Route className="w-4 h-4 text-gray-500" />;
      case "entrance":
        return <DoorOpen className="w-4 h-4 text-blue-600" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#EFDF00] border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-black text-white p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/10"
              onClick={() => navigate("/admin")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
            <div className="flex items-center gap-2">
              <Grid3X3 className="w-6 h-6 text-[#EFDF00]" />
              <h1 className="text-xl font-bold">Reifenlager Editor</h1>
            </div>
          </div>
          <Button 
            variant="outline" 
            className="border-[#EFDF00] text-[#EFDF00] hover:bg-[#EFDF00] hover:text-black"
            onClick={() => setSettingsDialog(true)}
          >
            <Settings className="w-4 h-4 mr-2" />
            Grid-Größe
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-white p-4 mb-4 flex flex-wrap items-center gap-6 text-sm">
          <span className="font-bold">Legende:</span>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-100 border-2 border-green-400"></div>
            <span>Lagerort</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-200 border-2 border-gray-400"></div>
            <span>Gang/Pfad</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-100 border-2 border-blue-400"></div>
            <span>Ein-/Ausgang</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-white border-2 border-gray-300"></div>
            <span>Leer</span>
          </div>
          <div className="flex items-center gap-2 ml-auto text-gray-500">
            <span className="text-xs bg-yellow-100 px-2 py-1 rounded">Tipp: Kacheln per Drag & Drop verschieben</span>
          </div>
        </div>

        {/* Grid */}
        <div className="bg-white p-6 overflow-auto">
          <div 
            className="grid gap-1 mx-auto"
            style={{
              gridTemplateColumns: `repeat(${gridWidth}, minmax(60px, 80px))`,
              width: 'fit-content'
            }}
          >
            {Array.from({ length: gridHeight }).map((_, y) =>
              Array.from({ length: gridWidth }).map((_, x) => {
                const key = `${x}-${y}`;
                const tile = tiles[key];
                const isDragging = draggedTile?.x === x && draggedTile?.y === y;
                const isDragOver = dragOverTile?.x === x && dragOverTile?.y === y;
                
                return (
                  <div
                    key={key}
                    draggable={!!tile}
                    onDragStart={(e) => handleDragStart(e, x, y)}
                    onDragOver={(e) => handleDragOver(e, x, y)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, x, y)}
                    onDragEnd={handleDragEnd}
                    className={`
                      aspect-square border-2 cursor-pointer transition-all
                      flex flex-col items-center justify-center p-1
                      ${getTileColor(tile)}
                      ${selectedTile?.x === x && selectedTile?.y === y ? 'ring-2 ring-[#EFDF00]' : ''}
                      ${isDragging ? 'opacity-50 scale-95' : ''}
                      ${isDragOver ? 'ring-2 ring-blue-500 bg-blue-50 scale-105' : ''}
                      ${tile ? 'cursor-grab active:cursor-grabbing' : ''}
                    `}
                    onClick={() => !isDragging && handleTileClick(x, y)}
                  >
                    {getTileIcon(tile)}
                    {tile?.tile_type === "storage" && (
                      <>
                        <span className="text-xs font-bold truncate w-full text-center mt-1">
                          {tile.name || `P${x}${y}`}
                        </span>
                        <span className="text-xs text-gray-500">
                          {tile.capacity}x
                        </span>
                      </>
                    )}
                    {tile?.tile_type === "entrance" && (
                      <span className="text-xs font-bold">E/A</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>
              Kachel bearbeiten ({selectedTile?.x}, {selectedTile?.y})
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Kacheltyp</Label>
              <Select 
                value={editForm.tile_type} 
                onValueChange={(v) => setEditForm(p => ({ ...p, tile_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="storage">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-green-600" />
                      Lagerort
                    </div>
                  </SelectItem>
                  <SelectItem value="path">
                    <div className="flex items-center gap-2">
                      <Route className="w-4 h-4 text-gray-500" />
                      Gang/Pfad
                    </div>
                  </SelectItem>
                  <SelectItem value="entrance">
                    <div className="flex items-center gap-2">
                      <DoorOpen className="w-4 h-4 text-blue-600" />
                      Ein-/Ausgang
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {editForm.tile_type === "storage" && (
              <>
                <div>
                  <Label>Name des Lagerplatzes</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="z.B. A1, Regal-Links, Ecke-Hinten"
                  />
                </div>
                <div>
                  <Label>Kapazität (Anzahl Reifen)</Label>
                  <Input
                    type="number"
                    value={editForm.capacity}
                    onChange={(e) => setEditForm(p => ({ ...p, capacity: parseInt(e.target.value) || 0 }))}
                    min={1}
                    max={100}
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-between">
            {selectedTile?.existing && (
              <Button variant="destructive" onClick={deleteTile}>
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setEditDialog(false)}>
                Abbrechen
              </Button>
              <Button 
                className="bg-[#EFDF00] text-black hover:bg-[#D9C900]"
                onClick={saveTile}
              >
                <Save className="w-4 h-4 mr-2" />
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Grid-Einstellungen</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Breite (Spalten)</Label>
              <Input
                type="number"
                value={gridWidth}
                onChange={(e) => setGridWidth(parseInt(e.target.value) || 10)}
                min={5}
                max={30}
              />
            </div>
            <div>
              <Label>Höhe (Zeilen)</Label>
              <Input
                type="number"
                value={gridHeight}
                onChange={(e) => setGridHeight(parseInt(e.target.value) || 10)}
                min={5}
                max={30}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSettingsDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              className="bg-[#EFDF00] text-black hover:bg-[#D9C900]"
              onClick={saveSettings}
            >
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TireMapEditor;
